import { Select, Text, Textarea } from '@mantine/core'
import { useForm } from '@mantine/form'
import { invariant } from '@seen/core/invariant'
import type { IDT } from '@seen/core/models/care-teams/idt'
import type { SDR } from '@seen/core/models/tasks/sdr/sdr'
import {
  SDRStatus,
  sdrStatusToMetadata,
} from '@seen/core/models/tasks/sdr/utils'
import { now } from '@seen/core/utils'
import { Button } from '@seen/ui'
import type { JSX } from 'react'
import { SDRIDTForm } from '#src/components/sdr/display/SDRIDTForm.tsx'
import { useIsReadOnly } from '#src/hooks/useIsReadOnly.ts'
import { usePatchMutation } from '#src/hooks/usePatchMutation.ts'

type FormData = {
  dispositionOutcome: SDR['dispositionOutcome']
  involvedIDTMember: SDR['dispositionInvolvedIDTMember']
  rationale: SDR['dispositionRationale']
}

type Props = {
  sdr: SDR
  careTeam: IDT | null
}

export function SDRSetDisposition({ sdr, careTeam }: Props): JSX.Element {
  const form = useForm<FormData>({
    mode: 'uncontrolled',
    initialValues: {
      dispositionOutcome: sdr.dispositionOutcome,
      involvedIDTMember: sdr.dispositionInvolvedIDTMember,
      rationale: sdr.dispositionRationale,
    },

    // functions will be used to validate values at corresponding key
    validate: {
      dispositionOutcome: (value) =>
        value != null ? null : 'Disposition outcome is required',
      involvedIDTMember: (value) =>
        value != null ? null : 'At least one IDT member must be selected',
      rationale: (value = '') =>
        value.trim().length > 0 ? null : 'Rationale is required',
    },
  })

  const isReadOnly = useIsReadOnly()
  const patchMutation = usePatchMutation<SDR>()

  return (
    <form
      onSubmit={form.onSubmit(
        (idtReviewData) => {
          patchMutation.mutate({
            instance: sdr,
            mutateFn: (sdr) => {
              invariant(
                idtReviewData.dispositionOutcome,
                'Disposition outcome is required'
              )

              const isChangingOutcome =
                sdr.dispositionOutcome !== idtReviewData.dispositionOutcome

              sdr.dispositionOutcome = idtReviewData.dispositionOutcome
              sdr.dispositionInvolvedIDTMember = idtReviewData.involvedIDTMember
              sdr.dispositionRationale = idtReviewData.rationale

              if (isChangingOutcome || !sdr.dispositionDate) {
                sdr.dispositionDate = now()
              }

              sdr.state = idtReviewData.dispositionOutcome
            },
          })
        },
        (err) => {
          console.error('Error:', err)
        }
      )}
    >
      <div className="flex flex-col gap-4">
        <Text className="text-lg font-semibold">Set Disposition</Text>

        <span className="text-sm text-gray-500">
          Determine and set SDR disposition. Note the reason for the
          disposition.
        </span>

        {careTeam && (
          <SDRIDTForm
            label="Select relevant IDT member"
            className="grow"
            careTeam={careTeam}
            key={form.key('involvedIDTMember')}
            {...form.getInputProps('involvedIDTMember')}
          />
        )}

        <Select
          label="Set Disposition Outcome"
          data={(
            [
              SDRStatus.APPROVED,
              SDRStatus.PARTIALLY_APPROVED,
              SDRStatus.DENIED,
              SDRStatus.PARTIALLY_DENIED,
            ] as const
          ).map((status) => ({
            value: status,
            label: sdrStatusToMetadata[status].label,
          }))}
          placeholder="Select..."
          key={form.key('dispositionOutcome')}
          {...form.getInputProps('dispositionOutcome')}
        />

        <div className="flex flex-col gap-2">
          <Textarea
            label="Rationale"
            description="Document details and reason for the disposition outcome"
            placeholder="Enter rationale"
            key={form.key('rationale')}
            {...form.getInputProps('rationale')}
          />
          <Button
            type="submit"
            loading={patchMutation.isPending}
            disabled={isReadOnly}
            className="ml-auto"
          >
            Submit
          </Button>
        </div>

        {patchMutation.isError && (
          <Text c="red" className="text-sm">
            Failed to set disposition:{' '}
            {patchMutation.error instanceof Error
              ? patchMutation.error.message
              : 'An error occurred'}
          </Text>
        )}
      </div>
    </form>
  )
}
